import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { deleteJourney, duplicateJourney } from '../features/journeys/journeySlice';
import { Box, Center, Tbody, Text } from '@chakra-ui/react';
import { TableCustom, TableCustomHeading, TableCustomRow } from './ui/TableCustom';
import Select from 'react-select';
import LoadingOverlay from './LoadingOverlay';
import EditModal from './ui/EditModal';
import { getDistanceForJourney } from '../utilities/journeyAnalysisHelper';
import JourneyForm from './JourneyForm';

export default function JourneyTable(journeyData) {
    const { isLoading } = useSelector((state) => state.journeys);
    const { user } = useSelector((state) => state.auth);

    const [journeyToEdit, setJourneyToEdit] = useState();
    const [currentPageNumber, setCurrentPageNumber] = useState(0);
    const [currentPage, setCurrentPage] = useState([]);
    const [pageNoOptions, setPageNoOptions] = useState([]);

    function paginateJourneys(rawJourneys) {
        let paginatedJourneys = [];
        let maxJourneysPerPage = 10;

        for (let i = 0; i < rawJourneys.length; i += maxJourneysPerPage) {
            const pageChunk = rawJourneys.slice(i, i + maxJourneysPerPage);
            paginatedJourneys.push(pageChunk);
        }

        return paginatedJourneys;
    }

    useEffect(() => {
        if (journeyData.journeys.length === 0) {
            setCurrentPage([]);
            setCurrentPageNumber(0);
            setPageNoOptions([]);
            return;
        }

        let pages = paginateJourneys(journeyData.journeys.slice());
        setPageNoOptions(pages.map((_, index) => ({ value: index, label: `Page ${index + 1}` })));

        let validPageNumber = currentPageNumber;
        if (currentPageNumber >= pages.length || pages[currentPageNumber].length === 0) {
            validPageNumber = currentPageNumber > 0 ? currentPageNumber - 1 : 0;
        }

        setCurrentPage(pages[validPageNumber]);
        setCurrentPageNumber(validPageNumber);
    }, [journeyData]);

    function handlePageChange(e) {
        const newPageNumber = e.value;
        const newPage = paginateJourneys(journeyData.journeys.slice())[newPageNumber];

        setCurrentPage(newPage || []);
        setCurrentPageNumber(newPageNumber);
    }

    const dispatch = useDispatch();

    return (
        <>
            {currentPage.length > 0 ? (
                <TableCustom>
                    <TableCustomHeading
                        additionalElements={
                            user.preferences.isOperatorEnabled ?
                                ['Date', 'Distance', 'Duration', 'Operator', 'Note'] :
                                ['Date', 'Distance', 'Duration', 'Note']
                        }
                    />
                    <Tbody>
                        {currentPage.map((journey) => (
                            <TableCustomRow
                                key={journey._id} // Make sure to add a key for list items
                                id={journey._id}
                                from={journey.from}
                                to={journey.to}
                                cannotMap={journey.route.positions.length === 0}
                                date={journey.date}
                                distance={getDistanceForJourney(journey)}
                                length={journey.length}
                                operator={user.preferences.isOperatorEnabled ? journey.operator : undefined}
                                note={journey.note || ''}
                                editButtonOnClick={() => setJourneyToEdit(journey)}
                                duplicateButtonOnClick={() => dispatch(duplicateJourney(journey._id))}
                                deleteButtonOnClick={() => dispatch(deleteJourney(journey._id))}
                            />
                        ))}
                    </Tbody>
                </TableCustom>
            ) : (
                <Center>
                    <Box paddingBottom={4} paddingTop={4}>
                        <Text fontSize='lg'>No journeys to display.</Text>
                    </Box>
                </Center>
            )}

            {pageNoOptions.length > 1 && currentPage.length > 0 && (
                <Center>
                    <Box maxWidth="150px" marginTop={4} marginBottom={4}>
                        <Select
                            value={pageNoOptions[currentPageNumber]} // Controlled component
                            options={pageNoOptions}
                            onChange={handlePageChange}
                        />
                    </Box>
                </Center>
            )}

            {journeyToEdit && (
                <EditModal
                    itemToEdit={journeyToEdit}
                    headingText='Edit journey'
                >
                    <JourneyForm editPrefilledJourney={journeyToEdit} />
                </EditModal>
            )}

            {isLoading && <LoadingOverlay type="component" />}
        </>
    );
}
